import { useEffect, useState, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { apiFetch } from '../contexts/AuthContext';

interface Property {
  id: string;
  name: string;
  boundary: Array<{ lat: number; lon: number }>;
  strokeColor: string | null;
}

/**
 * Fetches property boundaries and renders them as closed polylines (no fill)
 * on the Google Map. Includes a toggle button positioned top-right.
 */
export function PropertyOverlay() {
  const map = useMap();
  const [properties, setProperties] = useState<Property[]>([]);
  const [visible, setVisible] = useState(false);
  const overlaysRef = useRef<google.maps.Polyline[]>([]);

  useEffect(() => {
    apiFetch<Property[]>('/api/admin/properties').then((res) => {
      if (res.success && res.data) setProperties(res.data);
    });
  }, []);

  useEffect(() => {
    if (!map) return;

    // Clear old
    overlaysRef.current.forEach((p) => p.setMap(null));
    overlaysRef.current = [];

    if (!visible) return;

    for (const prop of properties) {
      if (!prop.boundary || prop.boundary.length < 2) continue;
      const path = [...prop.boundary.map((p) => ({ lat: p.lat, lng: p.lon }))];
      if (path.length >= 3) path.push(path[0]);

      const polyline = new google.maps.Polyline({
        path,
        strokeColor: prop.strokeColor || '#FFD700',
        strokeWeight: 2,
        strokeOpacity: 0.8,
        map,
      });
      overlaysRef.current.push(polyline);
    }

    return () => {
      overlaysRef.current.forEach((p) => p.setMap(null));
    };
  }, [map, properties, visible]);

  if (properties.length === 0) return null;

  return (
    <div style={{
      position: 'absolute',
      top: 10,
      right: 10,
      zIndex: 5,
    }}>
      <button
        onClick={() => setVisible((v) => !v)}
        title={visible ? 'Hide property boundaries' : 'Show property boundaries'}
        style={{
          padding: '6px 10px',
          backgroundColor: visible ? 'rgba(255,215,0,0.9)' : 'rgba(0,0,0,0.7)',
          color: visible ? '#000' : '#FFD700',
          border: `1px solid ${visible ? '#FFD700' : 'rgba(255,215,0,0.4)'}`,
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
        <span style={{
          width: 10, height: 10, borderRadius: 2,
          border: `2px solid ${visible ? '#000' : '#FFD700'}`,
          backgroundColor: visible ? '#FFD700' : 'transparent',
          display: 'inline-block',
        }} />
        Property
      </button>
    </div>
  );
}
